import { create } from "zustand";
import { logger } from "@/utils/logger";
import {
  Creator,
  CreatorOptionNode,
  CREATOR_DATA_TYPES,
  StatsData,
  StoreAssetPairData
} from "@/types/Creator";
import { IStore } from "@/types/interfaces";
import {
  OG_BANK,
  DEFAULT_SCALE,
  GROUP_TYPES,
  PRODUCTS,
  GET_FILE_REQUEST
} from "@/constants";
import { useCartStore } from "@/stores";
import axios from "axios";

interface CreatorState {
  creatorsCache: Map<string, Creator>;
  currentCreatorId: string | null;
  currentCreator: Creator | null;
  dataHierarchy: CreatorOptionNode[];
  attachmentMap: Record<string, string[]>;
  creatorStoreData: Record<string, any>;
}

interface CreatorActions extends IStore {
  // Getters
  getCurrentCreatorId: () => string | null;
  getCurrentCreator: () => Creator | null;
  getCurrentPreferredThemeId: () => string | null;
  getCurrentPreferredBackground: () => string;
  getAttachmentsForNode: (
    node: CreatorOptionNode,
    selectedOptions: Record<string, CreatorOptionNode>
  ) => CreatorOptionNode[];
  findNodeByDirectory: (
    tree: CreatorOptionNode[],
    targetDir: string
  ) => CreatorOptionNode | undefined;
  findNodeByKey: (
    tree: CreatorOptionNode[],
    key: string
  ) => CreatorOptionNode | undefined;
  parseAttachmentPath: (path: string) => { baseDir: string; targetKey: string };
  // Actions
  loadCreator: (creatorId: string) => Promise<Creator | null>;
  getCreator: (creatorId: string) => Creator | null;
  clearCache: () => void;
  buildCreatorDataHierarchy: () => void;
  fetchSharedData: (key: string) => Promise<ArrayBuffer | null>;

  // Private methods
  fetchCreatorData: (creatorId: string) => Promise<Creator | null>;
  setDefaultByProductId: (id: string) => Promise<void>;

  gatStatsDataByIndexes: (data: Record<number, string>) => StatsData[];
}

const initialState: CreatorState = {
  creatorsCache: new Map(),
  currentCreatorId: null,
  currentCreator: null,
  dataHierarchy: [],
  attachmentMap: {},
  creatorStoreData: {}
};

export const useCreatorStore = create<CreatorState & CreatorActions>(
  (set: any, get: any) => ({
    ...initialState,

    initialize: async (): Promise<boolean> => {
      try {
        return true;
      } catch (error) {
        logger.error(
          "Failed to initialize CreatorStore",
          error,
          "CreatorStore"
        );
        return false;
      }
    },

    reset: () => {
      set((state: CreatorState) => ({
        ...state,
        creatorsCache: new Map(),
        currentCreatorId: null,
        currentCreator: null,
        dataHierarchy: []
      }));
    },

    getStatus: () => {
      return {
        isInitialized: true
      };
    },

    getCurrentCreatorId: () => {
      const state = get();
      return state.currentCreatorId;
    },

    getCurrentCreator: () => {
      const state = get();
      return state.currentCreator;
    },

    getCurrentPreferredThemeId: () => {
      const state = get();
      if (!state.currentCreator?.settings?.theme) {
        return null;
      }
      return state.currentCreator.settings.theme;
    },

    getCurrentPreferredBackground: () => {
      const state = get();
      if (!state.currentCreator?.settings?.background) {
        return "/resources/skyBox/victorian_01.png";
      }
      return `/resources/skyBox/${state.currentCreator.settings.background}`;
    },

    loadCreator: async (creatorId: string): Promise<Creator | null> => {
      try {
        const state = get();

        // Check cache first
        if (state.creatorsCache.has(creatorId)) {
          const cachedCreator = state.creatorsCache.get(creatorId) || null;
          set((state: CreatorState) => ({
            ...state,
            currentCreatorId: creatorId,
            currentCreator: cachedCreator
          }));
          return cachedCreator;
        }

        // Load creator data from API or local storage
        const creator = await get().fetchCreatorData(creatorId);

        await get().fetchCreatorStoreData(creatorId);

        if (creator) {
          try {
            // Fetch default OG bank data
            let ogBank: Creator | null = null;

            // Check if ogBank is already in cache
            if (state.creatorsCache.has(OG_BANK)) {
              ogBank = state.creatorsCache.get(OG_BANK) || null;
            } else {
              // Fetch dogBank data
              ogBank = await get().fetchCreatorData(OG_BANK);
              if (ogBank) {
                // Cache ogBank
                const newCache = new Map(state.creatorsCache);
                newCache.set(OG_BANK, ogBank);
                set((state: CreatorState) => ({
                  ...state,
                  creatorsCache: newCache
                }));
              }
            }

            // Merge ogBank children into the current creator
            if (ogBank && ogBank.children) {
              for (const [key, value] of Object.entries(ogBank.children)) {
                if (!creator.children[key]) {
                  creator.children[key] = value;
                } else {
                  creator.children[key].children = {
                    ...value.children,
                    ...creator.children[key].children
                  };
                }
              }
              creator.children = {
                ...ogBank.children,
                ...creator.children
              };
            }
          } catch (error) {
            logger.warn(
              "Failed to merge default creator children",
              error,
              "CreatorStore"
            );
            // Continue even if merging fails
          }

          set((state: CreatorState) => {
            const newCache = new Map(state.creatorsCache);
            newCache.set(creatorId, creator);
            return {
              ...state,
              creatorsCache: newCache,
              currentCreatorId: creatorId,
              currentCreator: creator
            };
          });
        }

        return creator;
      } catch (error) {
        logger.error("Failed to load creator", error, "CreatorStore");
        return null;
      }
    },

    getCreator: (creatorId: string) => {
      const state = get();
      return state.creatorsCache.get(creatorId) || null;
    },

    clearCache: () => {
      set((state: CreatorState) => ({
        ...state,
        creatorsCache: new Map(),
        currentCreatorId: null,
        currentCreator: null
      }));
    },

    fetchCreatorData: async (creatorId: string): Promise<Creator | null> => {
      try {
        const url = `${
          import.meta.env.VITE_DOMAIN
        }creatorsData/${creatorId}/${creatorId}.json`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        return data as Creator;
      } catch (error) {
        logger.error("Failed to fetch creator data", error, "CreatorStore");
        return null;
      }
    },

    fetchCreatorStoreData: async (creatorId: string): Promise<void> => {
      try {
        const url = `${import.meta.env.VITE_DOMAIN}staticData/OGStoreData.json`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (data && data[creatorId]) {
          const cartStore = useCartStore.getState();
          cartStore.setPricingData(data[creatorId].pricing);
          cartStore.setSelectedProduct({
            digitalPrice:
              data[creatorId].pricing[PRODUCTS.DIGITAL.key][DEFAULT_SCALE]
                .price,
            physicalPrice:
              data[creatorId].pricing[PRODUCTS.PHYSICAL.key][DEFAULT_SCALE]
                .physicalPrice,
            variantBaseSize:
              data[creatorId].pricing[PRODUCTS.DIGITAL.key][DEFAULT_SCALE]
                .variantBaseSize,
            scale: DEFAULT_SCALE
          });
          set((state: CreatorState) => ({
            ...state,
            creatorStoreData: data[creatorId]
          }));
        }
      } catch (error) {
        logger.error(
          "Failed to fetch creator store data",
          error,
          "CreatorStore"
        );
      }
    },

    fetchSharedData: async (key: string): Promise<ArrayBuffer | null> => {
      try {
        const state = get();
        const creatorId = state.currentCreatorId;
        if (!creatorId) {
          throw new Error("Creator ID is not set");
        }

        const url = `${import.meta.env.VITE_BACKEND}${GET_FILE_REQUEST}/${creatorId}/${key}`;
        const response = await axios.get(url);
        if (!response) {
          throw new Error(`HTTP error`);
        }
        if (response?.data.url) {
          const file = await axios.get(response.data.url, {
            responseType: "arraybuffer"
          });
          if (!file) {
            return null;
          }
          return file.data;
        }
        return null;
      } catch (error) {
        logger.error("Failed to fetch shared data", error, "CreatorStore");
        return null;
      }
    },

    buildCreatorDataHierarchy: () => {
      const creator = get().currentCreator;
      if (!creator) {
        return;
      }
      const rootChildren = creator.children as Record<string, any>;

      const createNode = (
        parentKey: string,
        parentDisplayName: string,
        key: string,
        node: any
      ): CreatorOptionNode => {
        const childrenObj = node.children as Record<string, any> | undefined;
        const childrenNodes: Record<string, CreatorOptionNode> | undefined =
          childrenObj
            ? Object.fromEntries(
                Object.entries(childrenObj).map(([childKey, childVal]) => [
                  childKey,
                  createNode(key, node.displayName, childKey, childVal)
                ])
              )
            : undefined;

        const optionNode = {
          key: key === parentKey ? `${parentKey}_${key}` : key,
          name:
            node.name === parentKey ? `${parentKey}_${node.name}` : node.name,
          displayName: node.displayName,
          path: node.masterFile,
          type: node.type,
          icon: node.icon,
          thumbnail: node.thumbnail,
          displayAsset: node.displayAsset,
          defaultAsset: node.defaultAsset,
          isEnabled: node.isEnabled ?? false,
          isRootCategory: node.isRootCategory ?? false,
          children: childrenNodes,
          directory: node.directory,
          attachmentData: node.attachmentData || {},
          parentKey,
          parentDisplayName,
          modelVariations: node.modelVariations,
          modelVariationsIcons: node.modelVariationsIcons,
          baseScale: node.baseScale,
          scaleRatio: node.scaleRatio,
          overrideSlots: node.overrideSlots || [],
          defaultVariation: node.defaultVariation,
          characterStats: node.characterStats,
          isVisible: node.isVisible ?? true,
          isPublished: (node.isEnabled && node.isPublished) ?? true,
          isEditable:
            node.type === CREATOR_DATA_TYPES.CHARACTER && !node.masterFile
              ? true
              : false,
          isFlipIcon: node.isFlipIcon || false,
          spacing: node.spacing,
          spaceValue: node.spaceValue,
          pairedAttachment: node.pairedAttachment,
          isMandatoryAttachment: node.isMandatoryAttachment || false
        } as CreatorOptionNode;

        return optionNode;
      };

      const menuItems: CreatorOptionNode[] = [];
      for (const [key, node] of Object.entries(rootChildren)) {
        const built = createNode(
          get().currentCreatorId as string,
          "",
          key,
          node
        );
        menuItems.push(built);
      }

      set((state: CreatorState) => ({
        ...state,
        dataHierarchy: menuItems,
        attachmentMap: creator.attachmentMap
      }));
    },

    getAttachmentsForNode: (
      node: CreatorOptionNode,
      selectedOptions: Record<string, CreatorOptionNode>
    ): CreatorOptionNode[] => {
      const children: CreatorOptionNode[] = [];
      const state = get();
      const currentCreator = state.currentCreator;
      const attachmentMap = currentCreator?.attachmentMap as
        | Record<string, string[]>
        | undefined;
      if (!node.directory || !attachmentMap || !attachmentMap[node.directory]) {
        return children;
      }
      // Build ancestry-based chain from provided selections (exclude root id)
      const rootId = state.currentCreatorId || "";
      const chain: string[] = [];
      let currentKey = rootId;
      while (true) {
        const selected = selectedOptions[currentKey];
        if (!selected) break;
        if (selected.type === CREATOR_DATA_TYPES.ATTACHMENT) {
          chain.push(selected.name);
        }
        currentKey = selected.name;
      }
      const tree = (state.dataHierarchy || {}) as CreatorOptionNode[];
      for (const [, value] of Object.entries(attachmentMap[node.directory])) {
        const { baseDir, targetKey } = get().parseAttachmentPath(value);
        const found = get().findNodeByDirectory(tree, baseDir);
        if (found) {
          // Build slot using only parents BEFORE the found key, then append current node key
          let slotParts = [...chain];
          const idx = slotParts.indexOf(found.name);
          if (idx >= 0) {
            slotParts = slotParts.slice(0, idx);
          }
          if (
            slotParts.length === 0 ||
            slotParts[slotParts.length - 1] !== node.key
          ) {
            slotParts.push(node.key);
          }
          const slot = slotParts.join("_");
          const built: CreatorOptionNode = {
            ...found,
            path:
              found.modelVariations && targetKey
                ? found.modelVariations[targetKey]
                : found.path,
            attachmentData: node.attachmentData || {},
            parentKey: node.key,
            slot,
            attachmentDirectory: node.directory,
            overrideSlots: node.overrideSlots || [],
            isFlipIcon: node.isFlipIcon || false,
            pairedAttachment: node.pairedAttachment,
            isMandatoryAttachment: node.isMandatoryAttachment
          };
          children.push(built);
        }
      }
      return children;
    },

    parseAttachmentPath: (
      path: string
    ): {
      baseDir: string;
      targetKey: string;
    } => {
      const splitPath = path.split("/variations/");
      return {
        baseDir: splitPath[0],
        targetKey: splitPath[1].includes("/")
          ? splitPath[1].split("/")[0]
          : splitPath[1]
      };
    },

    findNodeByDirectory: (
      tree: CreatorOptionNode[],
      targetDir: string
    ): CreatorOptionNode | undefined => {
      const recurse = (
        nodes: CreatorOptionNode[]
      ): CreatorOptionNode | undefined => {
        for (const node of nodes) {
          if (node.directory === targetDir) {
            return node;
          }
          if (node.children) {
            const allChildren = Object.values(node.children);
            const found = recurse(
              allChildren as unknown as CreatorOptionNode[]
            );
            if (found) return found;
          }
        }
        return undefined;
      };
      return recurse(tree);
    },

    findNodeByKey: (
      tree: CreatorOptionNode[],
      key: string
    ): CreatorOptionNode | undefined => {
      const recurse = (
        nodes: CreatorOptionNode[]
      ): CreatorOptionNode | undefined => {
        for (const node of nodes) {
          if (key && node.key === key) {
            return node;
          }
          if (node.children) {
            const allChildren = Object.values(node.children);
            const found = recurse(
              allChildren as unknown as CreatorOptionNode[]
            );
            if (found) return found;
          }
        }
        return undefined;
      };
      return recurse(tree);
    },

    setDefaultByProductId: async (id: string): Promise<void> => {
      const storeAssetPairs = get().creatorStoreData.storeAssetPairs as Record<
        string,
        StoreAssetPairData
      >;
      const asset = storeAssetPairs ? storeAssetPairs[id] : null;
      if (asset?.assetPair) {
        const split = asset?.assetPair.split(`/${GROUP_TYPES.CHARACTERS}`);
        const dataHierarchy = get().dataHierarchy;
        const sets = dataHierarchy.find(
          (data: CreatorOptionNode) => data.key === CREATOR_DATA_TYPES.SETS
        );
        if (sets) {
          sets.defaultAsset = `${split[0]}`;
          if (split[1]) {
            const node = get().findNodeByDirectory(
              dataHierarchy,
              `${split[0]}/${GROUP_TYPES.CHARACTERS}`
            );
            if (node) {
              node.defaultAsset = `${asset?.assetPair}`;
            }
          }
        }
      }
    },

    gatStatsDataByIndexes: (data: Record<number, string>): StatsData[] => {
      const statsStructure = get().creatorStoreData?.statsStructure as Record<
        string,
        StatsData
      >;
      const indexes = Object.keys(data).map(Number);
      const statsData: StatsData[] = [];
      if (statsStructure) {
        for (const index of indexes) {
          for (const [key, stats] of Object.entries(statsStructure)) {
            if (stats.index === index) {
              const statObj: StatsData = {
                name: key,
                ...stats,
                value: data[index]
              };
              statsData.push(statObj);
              break;
            }
          }
        }
      }
      return statsData;
    }
  })
);
